import { AIStarIcon } from '@app/components/icons/ai-star';
import { AngleLeftIcon } from '@app/components/icons/angle-left';
import { CheckmarkNewIcon } from '@app/components/icons/checkmark-new';
import { CloseNewIcon } from '@app/components/icons/close-new';
import { EditNewIcon } from '@app/components/icons/edit-new';
import { Spinner } from '@app/components/icons/spinner';
import { Crown } from '@app/components/nav-bar/crown';
import { FLAG_CONTENT_HUB_ENABLE_UPGRADE_BANNER } from '@app/feature-flags/flags';
import { useFlag } from '@app/feature-flags/use-flag';
import { useFlags } from '@app/hooks/use-flags';
import { selectContentHubConfig } from '@app/routes/content/selectors';
import { buildTitleFromDate } from '@app/routes/content/utils';
import { selectIsAiDisabled } from '@app/routes/organization/selectors';
import { getPathParams } from '@app/routes/utils';
import { useAppDispatch, useAppSelector } from '@app/store/store';
import { FLAG_POST_EVENT_ENABLE_CREATOR_LAB_CONSOLIDATION } from '@app/utilities/flags';
import { Tab } from '@headlessui/react';
import { useTranslation } from '@hopin-team/provider-translation';
import cx from 'classnames';
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Link, useLocation, useMatch } from 'react-router-dom';

import { useRecordingAnalytics } from '../hooks';
import {
  selectRecording,
  selectRecordingBackUrl,
  selectRecordingTitleEditState,
} from '../selector';

export const Header = ({
  tabs,
  contextMenu,
}: {
  tabs: string[];
  contextMenu?: ReactNode;
}) => {
  const location = useLocation();
  const flags = useFlags();
  const isConsolidationEnabled = useFlag(
    FLAG_POST_EVENT_ENABLE_CREATOR_LAB_CONSOLIDATION,
  );

  // Extract the correct tab from pathname, accounting for nested lab routes
  const pathSegments = location.pathname.split('/');
  const labIndex = pathSegments.indexOf('lab');
  const selectedTab =
    labIndex !== -1 ? 'lab' : pathSegments[pathSegments.length - 1] || '';

  const isUpgradeBannerEnabled =
    flags?.[FLAG_CONTENT_HUB_ENABLE_UPGRADE_BANNER];
  const config = useAppSelector(selectContentHubConfig);
  const isContentHubConfigLoaded = config.status === 'success';
  const hasContentHubCreatorLabAccess =
    config.hasContentHubCreatorLabAccess ?? false;

  const defaultIndex = tabs.indexOf(selectedTab);
  const { t } = useTranslation();
  const { trackRecordingEvent } = useRecordingAnalytics();
  const dispatch = useAppDispatch();
  const recording = useAppSelector(selectRecording);
  const backUrl = useAppSelector(selectRecordingBackUrl);
  const titleEditState = useAppSelector(selectRecordingTitleEditState);
  const isAIDisabled = useAppSelector(selectIsAiDisabled);
  const { org_id } = getPathParams(['org_id']);

  // Use React Router's useMatch to properly detect if we're in a nested lab route
  // This matches /org/:organizationSlug/content/recordings/:recordingId/lab/:tabType
  const isInLabSubRoute = useMatch(
    '/org/:organizationSlug/content/recordings/:recordingId/lab/:tabType',
  );

  // Check if we're already on the editor route
  const isInEditorRoute = useMatch(
    '/org/:organizationSlug/content/recordings/:recordingId/editor',
  );

  // For lab sub-routes, we need to go up 2 levels; for editor route, stay on current path; for other routes, go up 1 level
  const editorPath = isInLabSubRoute
    ? '../../editor'
    : isInEditorRoute
    ? '.'
    : '../editor';
  const labPath = isInLabSubRoute ? '../social-post' : '../lab/social-post';

  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const displayTitle = buildTitleFromDate({
    locale: 'en',
    title: recording?.title,
    startedAt: recording?.started_at || '',
  });

  const handleTitleSave = useCallback(() => {
    const title = inputRef.current?.value?.trim();
    if (recording?.id && title) {
      trackRecordingEvent('ch_recording_editor_title_updated');
      dispatch({
        type: '[recording] update title started',
        payload: {
          recordingId: recording.id,
          title,
        },
      });
    }
  }, [dispatch, recording, trackRecordingEvent]);

  const handleTitleCancel = useCallback(() => {
    setIsEditingTitle(false);
  }, []);

  useEffect(() => {
    if (titleEditState.status === 'success') {
      setIsEditingTitle(false);
    }
  }, [titleEditState.status]);

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditingTitle && inputRef.current) {
      inputRef.current.value = recording?.title || '';
      inputRef.current.focus();
    }
  }, [isEditingTitle, recording?.title]);

  const handleTitleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleTitleSave();
      } else if (e.key === 'Escape') {
        handleTitleCancel();
      }
    },
    [handleTitleSave, handleTitleCancel],
  );

  const handleTabClick = useCallback(
    (tab: string) => {
      trackRecordingEvent('ch_recording_tab_switched', { tab });
    },
    [trackRecordingEvent],
  );

  const tabClass = ({ selected }: { selected: boolean }) =>
    cx(
      'relative min-w-[100px] px-3 py-2 h-10 border border-solid border-spring-neutral-b0/20 rounded-full flex items-center justify-center no-underline',
      {
        'bg-spring-neutral-b4': selected,
        'bg-transparent': !selected,
      },
    );

  return (
    <div className="border-b border-spring-neutral-b0/10 flex flex-row items-center px-4 py-3 bg-spring-neutral-base">
      {/* Left Section */}
      <div className="flex flex-row items-center gap-4 flex-shrink-0">
        {backUrl ? (
          <a
            href={backUrl}
            className="flex items-center justify-center w-8 h-8 text-spring-neutral-b0 hover:text-spring-neutral-b0"
          >
            <AngleLeftIcon className="w-5 h-5" />
          </a>
        ) : (
          <Link
            to={`/org/${org_id}/content/library`}
            className="flex items-center justify-center w-8 h-8 text-spring-neutral-b0 hover:text-spring-neutral-b0"
          >
            <AngleLeftIcon className="w-5 h-5" />
          </Link>
        )}

        <Tab.Group
          defaultIndex={defaultIndex}
          as="div"
          className="flex flex-row gap-2"
        >
          <Tab
            as={Link}
            to={editorPath}
            className={tabClass}
            onClick={() => handleTabClick('editor')}
          >
            <span className="font-medium text-sm text-spring-neutral-b0">
              {t(
                `content-recording.tabs.${
                  isConsolidationEnabled ? 'lab' : 'editor'
                }`,
              )}
            </span>
          </Tab>
          {!isConsolidationEnabled && (
            <Tab
              as={Link}
              to={labPath}
              className={tabClass}
              onClick={() => handleTabClick('lab')}
            >
              {isUpgradeBannerEnabled &&
              isContentHubConfigLoaded &&
              !hasContentHubCreatorLabAccess ? (
                <Crown className="size-4 mr-1 mb-1" />
              ) : (
                !isAIDisabled && <AIStarIcon className="size-4 mr-1 mb-1" />
              )}
              <span className="font-medium text-sm text-spring-neutral-b0">
                {t(`content-recording.tabs.lab`)}
              </span>
              <div className="absolute bg-spring-primary px-2 py-0.5 rounded-full font-bold text-spring-neutral-b0 text-xxs leading-4 -top-2 -right-3 border border-spring-neutral-base ">
                {t(`content-recording.lab.beta`)}
              </div>
            </Tab>
          )}
        </Tab.Group>
      </div>

      {/* Center Section - Recording Title */}
      <div className="flex items-center gap-2 mx-4 flex-1 min-w-0 h-8">
        {isEditingTitle ? (
          <input
            ref={inputRef}
            type="text"
            defaultValue={recording?.title || ''}
            onKeyDown={handleTitleKeyDown}
            className="text-xl font-medium text-spring-neutral-b0 bg-transparent border border-gray-650 rounded px-1 focus:outline-none focus:border-spring-primary w-full h-full"
          />
        ) : (
          <span className="text-xl font-medium text-spring-neutral-b2 truncate h-full flex items-center">
            {displayTitle}
          </span>
        )}
        {isEditingTitle ? (
          <div className="flex items-center gap-2 flex-shrink-0">
            {titleEditState.status === 'processing' ? (
              <Spinner size="tiny" theme="dark" />
            ) : (
              <CheckmarkNewIcon
                className="size-5 cursor-pointer text-white"
                onClick={handleTitleSave}
                data-testid="header-save-title-button"
              />
            )}
            <CloseNewIcon
              className="size-5 cursor-pointer text-white"
              onClick={handleTitleCancel}
              data-testid="header-cancel-title-button"
            />
          </div>
        ) : (
          <EditNewIcon
            className="size-5 cursor-pointer text-white flex-shrink-0"
            onClick={() => setIsEditingTitle(true)}
            data-testid="header-edit-title-button"
          />
        )}
      </div>

      {/* Right Section - Context Menu */}
      <div className="flex items-center flex-shrink-0">{contextMenu}</div>
    </div>
  );
};
